import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Briefcase } from 'lucide-react';
import { API_URL } from '../../../config/api';

interface ApplicationFormProps {
    jobTitle: string;
}

const ApplicationForm = ({ jobTitle }: ApplicationFormProps) => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: ''
    });

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('No file chosen');

    const [status, setStatus] = useState({
        loading: false,
        message: "",
        type: ""
    });

    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    // OTP State
    const [isVerified, setIsVerified] = useState(false);
    const [otpMode, setOtpMode] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpStatus, setOtpStatus] = useState({ loading: false, message: "", type: "" });

    const handleSendOTP = async () => {
        if (!formData.email) {
            setOtpStatus({ loading: false, message: "Please enter your email address first.", type: "error" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setOtpStatus({ loading: false, message: "Please enter a valid email address.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/request-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier: formData.email, context: "contact" })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setOtpMode(true);
                setOtpStatus({ loading: false, message: "Verification code sent to your email.", type: "success" });
            } else {
                setOtpStatus({ loading: false, message: data.message || "Failed to send code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Send Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length < 6) {
            setOtpStatus({ loading: false, message: "Please enter a valid 6-digit code.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email, otp, identifier: formData.email })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setIsVerified(true);
                setOtpMode(false);
                setOtpStatus({ loading: false, message: "Email verified successfully!", type: "success" });
                setTimeout(() => setOtpStatus({ loading: false, message: "", type: "" }), 3000);
            } else {
                setOtpStatus({ loading: false, message: data.message || "Invalid or expired code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Verify Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                e.target.value = '';
                return;
            }
            const allowedTypes = ['.pdf', '.doc', '.docx'];
            const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!allowedTypes.includes(fileExt)) {
                alert('Only PDF, DOC, and DOCX files are allowed');
                e.target.value = '';
                return;
            }
            setUploadedFile(file);
            setFileName(file.name);
        } else {
            setUploadedFile(null);
            setFileName('No file chosen');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!uploadedFile) {
            alert('Please upload your CV/Resume');
            return;
        }

        if (!isVerified) {
            setStatus({ loading: false, message: "Please verify your email address before submitting.", type: "error" });
            return;
        }

        if (!recaptchaToken) {
            setStatus({ loading: false, message: "Please complete the reCAPTCHA verification.", type: "error" });
            return;
        }

        setStatus({ loading: true, message: "", type: "" });

        const baseUrl = API_URL;

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('fullName', formData.fullName);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('jobTitle', jobTitle);
            formDataToSend.append('file', uploadedFile);
            formDataToSend.append('recaptchaToken', recaptchaToken);

            const res = await fetch(`${baseUrl}/application/submitApplication`, {
                method: "POST",
                body: formDataToSend,
            });

            const data = await res.json();

            if (res.ok) {
                setStatus({
                    loading: false,
                    message: "Application submitted successfully! We'll review your application and get back to you soon.",
                    type: "success"
                });
                setFormData({ fullName: '', email: '', phone: '' });
                setUploadedFile(null);
                setFileName('No file chosen');
                setRecaptchaToken("");
                setIsVerified(false);
                setOtpMode(false);
                setOtp("");

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                setTimeout(() => {
                    setStatus({ loading: false, message: "", type: "" });
                }, 5000);
            } else {
                throw new Error(data.message || "Failed to submit application");
            }
        } catch (error) {
            console.error("Error:", error);
            setStatus({
                loading: false,
                message: "Failed to submit application. Please try again.",
                type: "error"
            });
        }
    };

    const inputClass = "peer w-full bg-transparent border-2 border-purple-500/20 rounded-lg px-4 pt-6 pb-2 text-white focus:outline-none focus:border-purple-500/50 transition-colors disabled:opacity-50";
    const labelClass = `absolute left-4 top-4 text-gray-300 transition-all duration-200 
        peer-placeholder-shown:top-4 peer-placeholder-shown:text-base
        peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400
        peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs`;

    return (
        <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8">
            <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-2 rounded">
                    <Briefcase size={24} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold">Apply Now</h2>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Full Name */}
                <div className="relative">
                    <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        disabled={status.loading}
                        className={inputClass}
                        placeholder=" "
                        required
                    />
                    <label htmlFor="fullName" className={labelClass}>Full Name *</label>
                </div>

                {/* Email Address */}
                <div className="relative group">
                    <div className="relative">
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={status.loading || isVerified}
                            className={`${inputClass} pr-24`}
                            placeholder=" "
                            required
                        />
                        <label htmlFor="email" className={labelClass}>Email Address *</label>

                        {/* Action Button / Tick Mark */}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center h-full pt-2">
                            {formData.email && !isVerified && !otpMode && (
                                <button
                                    type="button"
                                    onClick={handleSendOTP}
                                    disabled={otpStatus.loading}
                                    className="flex items-center justify-center w-8 h-8 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded-lg border border-blue-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Verify Email"
                                >
                                    {otpStatus.loading ? (
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </button>
                            )}
                            {(isVerified || otpMode) && (
                                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.2)] animate-in fade-in zoom-in duration-500">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                        </div>

                        {otpMode && (
                            <div className="mt-4 p-4 bg-white/5 border border-purple-500/20 rounded-xl space-y-4">
                                <div className="relative">
                                    <input
                                        type="text"
                                        maxLength={6}
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        className="peer w-full px-4 py-3 bg-white/5 border border-purple-500/20 rounded-xl text-white text-center text-2xl tracking-widest focus:outline-none focus:border-purple-500 transition-all"
                                        placeholder="000000"
                                    />
                                    <label className="block text-center text-xs text-gray-400 mt-2 uppercase tracking-wider">
                                        Enter 6-digit Code
                                    </label>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleVerifyOTP}
                                        disabled={otpStatus.loading}
                                        className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                                    >
                                        {otpStatus.loading ? "Verifying..." : "Verify Code"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOtpMode(false)}
                                        className="px-4 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {otpStatus.message && !isVerified && (
                        <p className={`mt-2 text-sm px-2 ${otpStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                            {otpStatus.message}
                        </p>
                    )}
                </div>

                {/* Phone Number */}
                <div className="relative">
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={status.loading}
                        className={inputClass}
                        placeholder=" "
                        required
                    />
                    <label htmlFor="phone" className={labelClass}>Phone Number *</label>
                </div>

                {/* File Upload */}
                <div>
                    <label className="block w-full">
                        <div className={`border-2 border-purple-500/20 rounded-lg px-4 py-3 cursor-pointer hover:border-purple-500/50 transition-colors ${status.loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                                disabled={status.loading}
                            />
                            <div className="flex items-center justify-between">
                                <span className="text-gray-300">Choose File</span>
                                <span className={`text-sm ${uploadedFile ? 'text-purple-400' : 'text-gray-500'}`}>
                                    {fileName}
                                </span>
                            </div>
                        </div>
                    </label>
                    <p className="text-gray-500 text-sm mt-2">Max size: 10MB. Allowed: PDF, DOC, DOCX *</p>
                </div>

                {/* Success/Error Message */}
                {status.message && (
                    <div className={`p-4 rounded-lg ${status.type === 'success'
                        ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                        : 'bg-red-500/20 border border-red-500/50 text-red-400'
                        }`}>
                        <div className="flex items-center gap-2">
                            {status.type === 'success' ? (
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            )}
                            <span>{status.message}</span>
                        </div>
                    </div>
                )}

                {/* Captcha */}
                <div className="flex justify-center flex-col items-center gap-4">
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                        theme="dark"
                        onChange={(token) => setRecaptchaToken(token || "")}
                    />
                    {!recaptchaToken && (
                        <p className="text-gray-500 text-xs">Verification required to prevent spam</p>
                    )}
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={status.loading || !isVerified || !recaptchaToken}
                    className="w-full py-4 px-6 bg-gradient-to-r from-indigo-500 to-purple-500 hover:opacity-90 text-white font-semibold rounded-xl shadow-lg hover:shadow-purple-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0"
                >
                    {status.loading ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
                </button>

                {/* Alternative Email */}
                {/* <p className="text-gray-300 text-sm text-center">
                    Or email your CV to{' '}
                    <a
                        href={`mailto:career@sanmicron.com?subject=Application for ${jobTitle}`}
                        className="text-purple-400 hover:underline"
                    >
                        career@sanmicron.com
                    </a>
                </p> */}
            </form>
        </div>
    );
};

export default ApplicationForm;